<template>
  <div class="my-1 sm:my-3">
    <button
      id="button"
      :disabled="isSending"
      @click="toggleRecording"
      v-show="!isPlaying"
      class="bg-gray-300 hover:bg-gray-400 text-gray-700 shadow-md font-semibold capitalize rounded-full p-2 lg:p-3"
      :style="{ backgroundColor: chatStore?.primaryColor }"
      >
      <span v-if="isSending" class="flex items-center space-x-2">
        <svg
          class="text-white animate-spin"
          viewBox="0 0 64 64"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
        >
          <path
            d="M32 3C35.8083 3 39.5794 3.75011 43.0978 5.20749C46.6163 6.66488 49.8132 8.80101 52.5061 11.4939C55.199 14.1868 57.3351 17.3837 58.7925 20.9022C60.2499 24.4206 61 28.1917 61 32C61 35.8083 60.2499 39.5794 58.7925 43.0978C57.3351 46.6163 55.199 49.8132 52.5061 52.5061C49.8132 55.199 46.6163 57.3351 43.0978 58.7925C39.5794 60.2499 35.8083 61 32 61C28.1917 61 24.4206 60.2499 20.9022 58.7925C17.3837 57.3351 14.1868 55.199 11.4939 52.5061C8.801 49.8132 6.66487 46.6163 5.20749 43.0978C3.7501 39.5794 3 35.8083 3 32C3 28.1917 3.75011 24.4206 5.2075 20.9022C6.66489 17.3837 8.80101 14.1868 11.4939 11.4939C14.1868 8.80099 17.3838 6.66487 20.9022 5.20749C24.4206 3.7501 28.1917 3 32 3L32 3Z"
            stroke="currentColor"
            stroke-width="5"
            stroke-linecap="round"
            stroke-linejoin="round"
          ></path>
          <path
            d="M32 3C36.5778 3 41.0906 4.08374 45.1692 6.16256C49.2477 8.24138 52.7762 11.2562 55.466 14.9605C58.1558 18.6647 59.9304 22.9531 60.6448 27.4748C61.3591 31.9965 60.9928 36.6232 59.5759 40.9762"
            stroke="currentColor"
            stroke-width="5"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="text-gray-900"
          ></path>
        </svg>
      </span>
      <template v-else>
        <MicIcon
          v-show="!isRecording"
          :customClass="
            customIconClass ||
            'w-7 h-7 md:w-6 md:h-6 lg:w-14 lg:h-14 text-white'
          "
        />
        <StopIcon
          v-show="isRecording"
          :customClass="
            customIconClass ||
            'w-7 h-7 md:w-6 md:h-6 lg:w-14 lg:h-14 text-white'
          "
        />
      </template>
    </button>
    <button
      id="button"
      v-show="isPlaying"
      @click="stopSpeaking"
      style="background-color: #3a1a55"
      class="bg-gray-300 hover:bg-gray-400 text-gray-700 shadow-md font-semibold capitalize rounded-full p-2 lg:p-3"
    >
      <SpeakerIcon
        :customClass="
          customIconClass || 'w-7 h-7 md:w-6 md:h-6 lg:w-14 lg:h-14 text-white'
        "
      />
    </button>
  </div>
</template>

<script>
import { getAudioStream, speechToText } from "../api/processVideoRequest";
import useChatbotStore from "@/store/chatbot";
import Swal from "sweetalert2";
import MicIcon from "@/Icons/MicIcon.vue";
import StopIcon from "@/Icons/StopIcon.vue";
import SpeakerIcon from "@/Icons/SpeakerIcon.vue";
import { askQuestion } from "@/api/userRequests";
import { Howl, Howler } from "howler";

export default {
  name: "AudioRecorder",
  components: { MicIcon, StopIcon, SpeakerIcon },
  props: {
    customIconClass: {
      type: String,
      default: "w-7 h-7 md:w-6 md:h-6 lg:w-14 lg:h-14",
    },
  },
  data() {
    return {
      mediaRecorder: null,
      audioChunks: [],
      isRecording: false,
      isSending: false,
      isPlaying: false,
      chatStore: useChatbotStore(), // Use the store in the data option

      audioHowler: null,
      promptAudioHowler: null,
    };
  },
  methods: {
    async startRecording() {
      try {
        // Check if the browser supports media devices and MediaRecorder
        if (
          !navigator?.mediaDevices ||
          !navigator?.mediaDevices?.getUserMedia
        ) {
          Swal.fire({
            icon: "error",
            title: "Your browser does not support audio recording.",
          });
          return;
        }

        if (typeof MediaRecorder === "undefined") {
          Swal.fire({
            icon: "error",
            title: "Your browser does not support MediaRecorder.",
          });
          return;
        }

        this.$emit("pauseVideo");
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        this.mediaRecorder = new MediaRecorder(stream);

        this.mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            this.audioChunks.push(event.data);
          }
        };

        this.mediaRecorder.onstop = async () => {
          await this.sendAudioToServer();
        };

        this.mediaRecorder.start();
        this.isRecording = true;
      } catch (err) {
        // Check if the error is due to permission issues
        if (
          err?.name === "NotAllowedError" ||
          err?.name === "PermissionDeniedError"
        ) {
          Swal.fire({
            icon: "error",
            title: "Microphone permission is required to start recording.",
          });
        }

        console.error("Error:", err);
      }
    },

    async stopRecording() {
      if (this.mediaRecorder && this.isRecording) {
        this.mediaRecorder.stop();
        this.isRecording = false;

        // Stop all audio tracks
        await this.mediaRecorder.stream
          .getTracks()
          .forEach((track) => track.stop());

        // await this.sendAudioToServer();
      }
    },

    toggleRecording() {
      if (this.chatStore.isLastTry) {
        Swal.fire({
          icon: "error",
          title: "Enter your email in chat to continue using the AI Assistant.",
        });
        return;
      }
      if (this.isRecording) {
        this.stopRecording();
      } else {
        this.audioChunks = []; // Clear previous recording
        this.startRecording();
      }
    },

    async sendAudioToServer() {
      try {
        this.isSending = true;
        const audioBlob = new Blob(this.audioChunks, { type: "audio/webm" });
        const formData = new FormData();
        formData.append("audio", audioBlob);

        const response = await speechToText(formData, {
          "Content-Type": "multipart/form-data",
        });

        console.log("user message: ", response?.data?.text);

        this.chatStore.setMessage(
          {
            sender: "user",
            text: response?.data?.text,
            type: "text",
          }
          // undefined,
          // recMessageId
        );

        await this.playPreloadPrompt();

        const text = await this.processVideoQuestion(response?.data?.text);

        console.log("bot answer: ", text);

        const initAudioHowlerResponse = await this.initAudioHowler(text);

        if (!initAudioHowlerResponse) {
          return;
        }
        if (this.promptAudioHowler && this.promptAudioHowler?.playing)
          this.promptAudioHowler?.pause();

        this.audioHowler?.play();

        this.isLoading = false;
        this.chatStore.increaseTry();

        this.chatStore.setMessage({
          sender: "bot",
          text: text,
          type: "text",
          playAudio: true,
        });

        // Clear recording after successful send
        this.audioChunks = [];
      } catch (err) {
        console.error("Error:", err);
      } finally {
        if (this.isSending) this.isSending = false;
        console.log("processed audio in audio recorder");
      }
    },
    async processVideoQuestion(question) {
      // this.chatStore.setLoading(true);

      try {
        const response = await askQuestion({
          videoId: this.chatStore?.videoDetails?.id,
          question: question,
          userId: this.chatStore.user?._id,
        });
        const answer = response?.data?.answer;

        console.log("Ask Question API call successful:", response.data);
        return answer;
      } catch (error) {
        console.error("Ask Question API call error:", error);
        throw error;
      } finally {
        // this.chatStore.setLoading(false);
      }
    },
    async initAudioHowler(text) {
      try {
        // Fetch audio from the server as a blob
        const response = await getAudioStream(
          {
            voiceId: this.chatStore?.videoDetails?.voiceId,
            videoId: this.chatStore?.videoDetails?.id,
            clientId: this.chatStore?.videoDetails?.clientId,
            userId: this.chatStore?.user?._id,
            message: text,
          },
          {
            responseType: "blob", // Ensures the response is a arraybuffer
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        // Convert the Axios blob response to an object URL
        const audioBlob = response.data;
        const audioUrl = URL.createObjectURL(
          new Blob([audioBlob], { type: "audio/mpeg" })
        );

        // Create a new Howl instance and set the audio source
        this.audioHowler = new Howl({
          src: [audioUrl],
          html5: true, // Ensures that the audio is played via HTML5 audio (for large files)
          format: ["mp3"], // Manually specify the format here (e.g., 'mp3', 'ogg', 'wav')
          autoUnlock: true,
          autoplay: false, // Start playing programmatically
          preload: true, // Preload audio
          rate: this.chatStore?.videoDetails?.videoSettings?.voiceSpeed ?? 1,
          // mute: true,
          onplay: () => {
            this.isPlaying = true;
            console.log("howler ctx : ", Howler?.ctx?.state);
            this.$emit("toggleVideo", "pause");
          },
          onpause: () => {
            this.isPlaying = false;
            this.$emit("toggleVideo", "play");
          },
          onend: () => {
            this.isPlaying = false;
            this.$emit("toggleVideo", "play");
          },
          onloaderror: (id, err) => {
            console.log("Failed to load sound : ", id, err);
          },
          onplayerror: (id, err) => {
            console.log("Failed to play sound : ", id, err);
          },
          onunlock: function () {
            console.log("custom controls player: howler ctx is unlocked");
          },
        });
        return true;
      } catch (error) {
        console.log("erroe", error);
        if (error?.response?.data instanceof Blob) {
          try {
            // Parse Blob to JSON
            const errorData = await error.response.data.text();
            const parsedError = JSON.parse(errorData);

            // Check for specific error status
            if (parsedError?.status === "SESSION_MINUTES_LIMIT_REACHED") {
              Swal.fire({
                icon: "error",
                title: "Audio Limit Reached",
                text:
                  parsedError.message || "You have exceeded your audio limit.",
              });
            } else {
              Swal.fire({
                icon: "error",
                title: "Error",
                text: parsedError.message || "An unexpected error occurred.",
              });
            }
          } catch (parseError) {
            console.error("Error parsing blob:", parseError);
            Swal.fire({
              icon: "error",
              title: "Error",
              text: "An unexpected error occurred while processing your request.",
            });
          }
        }
      }
      return false;
    },
    async playPreloadPrompt() {
      if (
        !this.chatStore?.videoDetails?.videoSettings?.useInterruptiblePrompts
      ) {
        return;
      }

      if (
        !Array.isArray(this.chatStore?.videoDetails?.audioUrls) ||
        this.chatStore?.videoDetails?.audioUrls.length === 0
      ) {
        return;
      }
      const url = this.getRandomAudioUrl(
        this.chatStore?.videoDetails?.audioUrls
      );
      if (url) {
        // Create a new Howl instance and set the audio source
        this.promptAudioHowler = new Howl({
          src: [url],
          html5: true, // Ensures that the audio is played via HTML5 audio (for large files)
          format: ["mp3"], // Manually specify the format here (e.g., 'mp3', 'ogg', 'wav')
          autoUnlock: true,
          autoplay: false, // Start playing programmatically
          preload: true, // Preload audio
          // rate: 1.2,
          // mute: true,
          onplay: () => {
            // this.isPlaying = true;
            console.log("howler ctx : ", Howler?.ctx?.state);
            this.$emit("toggleVideo", "pause");
          },
          onpause: () => {
            // this.isPlaying = false;
            // this.$emit("toggleVideo", "play");
          },
          onend: () => {
            // this.isPlaying = false;
            // this.$emit("toggleVideo", "play");
          },
          onloaderror: (id, err) => {
            console.log("Failed to load waiting sound : ", id, err);
          },
          onplayerror: (id, err) => {
            console.log("Failed to play waiting sound : ", id, err);
          },
          onunlock: function () {
            console.log("audio recorder component: howler ctx is unlocked");
          },
        });
        this.promptAudioHowler?.play();
      }
    },
    getRandomAudioUrl(arr) {
      const randomIndex = Math.floor(Math.random() * arr.length);
      return arr[randomIndex]?.audioUrl;
    },
    stopSpeaking() {
      this.$emit("resumeVideo");
      this.audioHowler?.pause();
    },
  },

  beforeUnmount() {
    this.stopRecording();
  },
};
</script>

<style scoped></style>
